import { useState, useEffect } from 'react'
import SectionCard from '@/components/dashboard/SectionCard'
import { EmptyState } from '@/components/dashboard/BarListWidget'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, X, Loader2, Calendar, Home, CheckCircle2, XCircle, Clock, ArrowRight } from 'lucide-react'
import {
  getMyBalance,
  listMyRequests,
  createLeaveRequest,
  cancelRequest,
  getEligibleTypes,
  getLeaveSettings,
  listApprovers,
  type LeaveType,
  type LeaveTypeConfig,
  type LeaveRequest,
} from '@/api/leaves'
import { useAuthStore } from '@/stores/authStore'

const LEAVE_TYPE_LABELS: Record<string, string> = {
  casual: 'Casual', sick: 'Sick', urgent: 'Urgent', annual: 'Annual', wfh: 'WFH',
}
const typeLabel = (key: string, configs?: LeaveTypeConfig[]) =>
  configs?.find((c) => c.key === key)?.label || LEAVE_TYPE_LABELS[key] || key

function StatusBadge({ status }: { status: LeaveRequest['status'] }) {
  const map: Record<LeaveRequest['status'], { label: string; cls: string; icon: any }> = {
    pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
    approved: { label: 'Approved', cls: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle2 },
    rejected: { label: 'Rejected', cls: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
    cancelled: { label: 'Cancelled', cls: 'bg-gray-50 text-gray-600 border-gray-200', icon: X },
  }
  const m = map[status]
  const Icon = m.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${m.cls}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  )
}

function BalanceCard({ label, color, isWfh, total, used, remaining, period, over, fy_used, pastYear }: {
  label: string; color: string; isWfh: boolean; total: number; used: number; remaining: number
  period?: 'month'; over?: number; fy_used?: number
  /** Showing a past fiscal year: monthly types show that year's total instead of "this month". */
  pastYear?: boolean
}) {
  const monthly = period === 'month'
  const Icon = isWfh ? Home : Calendar
  if (monthly && pastYear) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${color}22`, color }}>
              <Icon className="h-4 w-4" />
            </div>
            <span className="text-sm font-semibold text-gray-900">{label}</span>
          </div>
          <span className="text-xs text-gray-400">{total} per month</span>
        </div>
        <div className="w-full h-2 bg-gray-100 rounded-full" />
        <div className="mt-2 flex justify-between text-xs text-gray-500">
          <span>Used {fy_used ?? 0} in this year</span>
          <span>Monthly limit</span>
        </div>
      </div>
    )
  }
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: `${color}22`, color }}>
            <Icon className="h-4 w-4" />
          </div>
          <span className="text-sm font-semibold text-gray-900">{label}</span>
        </div>
        <span className="text-xs text-gray-400">{remaining}/{total} {monthly ? 'left this month' : 'days left'}</span>
      </div>
      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#EC5B2C] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 flex justify-between text-xs text-gray-500">
        <span>Used {used}{monthly ? ' this month' : ''}</span>
        <span>{monthly ? `${total} per month` : `Total ${total}`}</span>
      </div>
      {!!over && over > 0 && (
        <p className="mt-1 text-xs font-medium text-red-600">{over} over the monthly limit (approved as exception)</p>
      )}
    </div>
  )
}

export function ApplyModal({ onClose, onCreated, reversalOf, initialType }: { onClose: () => void; onCreated: () => void; reversalOf?: LeaveRequest; initialType?: LeaveType }) {
  const { user } = useAuthStore()
  // Only the types this user is eligible to request.
  const { data: leaveTypes = [] } = useQuery({ queryKey: ['leave-types', 'eligible', user?.id], queryFn: getEligibleTypes })
  const [type, setType] = useState<LeaveType>(reversalOf?.type || initialType || 'casual')
  // Default to the first configured type once loaded (handles renamed/custom types).
  useEffect(() => {
    if (leaveTypes.length && !leaveTypes.some((t) => t.key === type)) setType(leaveTypes[0].key)
  }, [leaveTypes]) // eslint-disable-line react-hooks/exhaustive-deps
  const [startDate, setStartDate] = useState(reversalOf?.start_date || '')
  const [endDate, setEndDate] = useState(reversalOf?.end_date || '')
  const [halfDay, setHalfDay] = useState(reversalOf?.half_day || false)
  const [halfDaySlot, setHalfDaySlot] = useState<'morning' | 'afternoon'>(
    (reversalOf?.half_day_slot as any) || 'morning'
  )
  const [reason, setReason] = useState(reversalOf ? `Reversal of leave on ${reversalOf.start_date}` : '')
  const [approverSearch, setApproverSearch] = useState('')
  const [approverId, setApproverId] = useState('')
  const [approverName, setApproverName] = useState('')
  const [error, setError] = useState('')
  // Server warning when the request exceeds a monthly allowance (e.g. 3rd WFH day);
  // the user can still submit it as an exception for the approver.
  const [overLimitMsg, setOverLimitMsg] = useState('')

  // How far back leave can be applied for (missed days), in the user's timezone.
  const { data: settings } = useQuery({ queryKey: ['leaves', 'settings', user?.id], queryFn: getLeaveSettings })
  const minDate = (() => {
    if (!settings || reversalOf) return undefined
    const d = new Date(`${settings.today}T00:00:00`)
    d.setDate(d.getDate() - settings.backdate_days)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })()

  const { data: approvers = [], isLoading: approversLoading } = useQuery({
    queryKey: ['leaves', 'approvers', user?.id, approverSearch],
    queryFn: () => listApprovers(approverSearch || undefined),
    staleTime: 30_000,
  })

  const createMut = useMutation({
    mutationFn: createLeaveRequest,
    onSuccess: () => { onCreated(); onClose() },
    onError: (err: any) => {
      if (err?.response?.status === 409 && err?.response?.data?.over_limit) {
        setOverLimitMsg(err.response.data.detail)
        return
      }
      setError(err?.response?.data?.detail || 'Failed to submit')
    },
  })

  const handleSubmit = (e: React.FormEvent, acknowledgeOverLimit = false) => {
    e.preventDefault()
    setError('')
    if (!acknowledgeOverLimit) setOverLimitMsg('')
    if (!startDate || !endDate) return setError('Start and end date required')
    if (endDate < startDate) return setError('End date must be on or after start date')
    if (halfDay && startDate !== endDate) return setError('Half-day only works for a single day')
    if (!approverId) return setError('Please select an approver')

    createMut.mutate({
      type,
      start_date: startDate,
      end_date: endDate,
      half_day: halfDay,
      half_day_slot: halfDay ? halfDaySlot : undefined,
      reason: reason || undefined,
      approver_id: approverId,
      reverses_request_id: reversalOf?.id,
      acknowledge_over_limit: acknowledgeOverLimit || undefined,
    })
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-bold text-gray-900">
            {reversalOf ? 'Reverse Leave' : 'Apply for Leave'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        {reversalOf && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 text-sm text-amber-800">
            This will cancel your approved leave from <strong>{reversalOf.start_date}</strong>
            {reversalOf.start_date !== reversalOf.end_date && <> to <strong>{reversalOf.end_date}</strong></>}
            {' '}once approved, and refund the quota.
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>}
          {overLimitMsg && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 space-y-2">
              <p>⚠️ {overLimitMsg}</p>
              <button
                type="button"
                onClick={(e) => handleSubmit(e as unknown as React.FormEvent, true)}
                disabled={createMut.isPending}
                className="px-3 py-1.5 text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50"
              >
                Submit anyway (needs approval)
              </button>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {leaveTypes.map((lt) => {
                const Icon = lt.key === 'wfh' ? Home : Calendar
                return (
                  <button
                    key={lt.key} type="button"
                    onClick={() => setType(lt.key)}
                    className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-all ${type === lt.key ? 'border-[#EC5B2C] bg-[#EC5B2C]/5' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <Icon className="h-4 w-4" style={{ color: lt.color }} />
                    <span className="text-xs font-medium">{lt.label}</span>
                  </button>
                )
              })}
            </div>
            {(() => {
              const cfg = leaveTypes.find((lt) => lt.key === type)
              if (!cfg) return null
              const bits: string[] = []
              if (cfg.effective_quota != null) bits.push(`${cfg.effective_quota} days/${cfg.quota_period === 'month' ? 'month' : 'year'}`)
              if (cfg.min_days != null) bits.push(`min ${cfg.min_days}d`)
              if (cfg.max_days != null) bits.push(`max ${cfg.max_days}d`)
              if (cfg.min_notice_days != null) bits.push(`${cfg.min_notice_days}d notice`)
              return bits.length ? <p className="mt-2 text-xs text-gray-400">{bits.join(' · ')}</p> : null
            })()}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input type="date" required value={startDate} min={minDate} onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EC5B2C]/30 focus:border-[#EC5B2C] text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input type="date" required value={endDate} min={startDate || minDate} onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EC5B2C]/30 focus:border-[#EC5B2C] text-sm" />
            </div>
          </div>
          {settings && !reversalOf && (
            <p className="-mt-2 text-xs text-gray-400">
              {settings.backdate_days > 0
                ? `Missed a day? You can apply for leave up to ${settings.backdate_days} days back.`
                : 'Leave can only be applied for today or future dates.'}
            </p>
          )}

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={halfDay} onChange={(e) => setHalfDay(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-[#EC5B2C] focus:ring-[#EC5B2C]" />
              Half day
            </label>
            {halfDay && (
              <select value={halfDaySlot} onChange={(e) => setHalfDaySlot(e.target.value as any)}
                className="px-2 py-1 border border-gray-300 rounded text-sm">
                <option value="morning">Morning</option>
                <option value="afternoon">Afternoon</option>
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EC5B2C]/30 focus:border-[#EC5B2C] text-sm" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Send to for approval</label>
            <input type="text" value={approverSearch} onChange={(e) => setApproverSearch(e.target.value)} placeholder="Search by name or email..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#EC5B2C]/30 focus:border-[#EC5B2C] text-sm mb-2" />
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {approvers.map((u) => (
                <button key={u.id} type="button"
                  onClick={() => { setApproverId(u.id); setApproverName(u.name) }}
                  className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left ${approverId === u.id ? 'bg-[#EC5B2C]/10' : ''}`}>
                  <div className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-600">
                    {u.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {u.name}
                      {u.is_manager && <span className="ml-1.5 rounded-full bg-[#EC5B2C]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#EC5B2C]">Your manager</span>}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{u.email}</p>
                  </div>
                  {approverId === u.id && <CheckCircle2 className="h-4 w-4 text-[#EC5B2C]" />}
                </button>
              ))}
              {!approversLoading && approvers.length === 0 && (
                <div className="px-3 py-4 text-sm text-gray-400 text-center">
                  {approverSearch
                    ? 'No approver matches that name or email'
                    : 'No approvers available — ask your admin to set your manager'}
                </div>
              )}
            </div>
            {approverName && <p className="text-xs text-gray-600 mt-1">Approver: <strong>{approverName}</strong></p>}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button type="submit" disabled={createMut.isPending}
              className="px-5 py-2 text-sm font-medium text-white bg-[#EC5B2C] hover:bg-[#EA4913] rounded-lg disabled:opacity-50 flex items-center gap-2">
              {createMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export function RequestRow({ req, onCancel, onReverse }: { req: LeaveRequest; onCancel: (id: string) => void; onReverse: (req: LeaveRequest) => void }) {
  const Icon = req.type === 'wfh' ? Home : Calendar
  const dateText = req.start_date === req.end_date ? req.start_date : `${req.start_date} → ${req.end_date}`
  return (
    <div className="bg-white p-4 flex items-center gap-4 hover:bg-gray-50/60 transition-colors">
      <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-gray-100 text-gray-600">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-gray-900 capitalize">{typeLabel(req.type)}</span>
          <StatusBadge status={req.status} />
          {req.half_day && <span className="text-xs text-gray-500">(½ {req.half_day_slot})</span>}
          {req.is_backdated && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">Backdated</span>}
          {req.is_direct && <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-medium text-sky-700">Instant</span>}
          {req.over_limit && <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-medium text-red-600">Over monthly limit</span>}
        </div>
        <p className="text-xs text-gray-500 mt-0.5">{dateText} · {req.working_days} day{req.working_days !== 1 ? 's' : ''}</p>
        {req.reason && <p className="text-xs text-gray-600 mt-1 line-clamp-1">{req.reason}</p>}
        {req.status === 'pending' && req.current_approver_name && (
          <p className="text-xs text-amber-700 mt-1">Waiting for <strong>{req.current_approver_name}</strong></p>
        )}
        {req.status === 'approved' && req.final_approver_name && (
          <p className="text-xs text-green-700 mt-1">Approved by <strong>{req.final_approver_name}</strong></p>
        )}
        {req.is_direct && (
          <p className="text-xs text-gray-500 mt-1">Recorded directly (Slack or check-in) — no approval needed</p>
        )}
        {req.status === 'rejected' && req.final_approver_name && (
          <p className="text-xs text-red-700 mt-1">Rejected by <strong>{req.final_approver_name}</strong></p>
        )}
      </div>
      {req.status === 'pending' && (
        <button onClick={() => onCancel(req.id)}
          className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
          Cancel
        </button>
      )}
      {req.status === 'approved' && !req.reverses_request_id && !req.is_direct && (
        <button onClick={() => onReverse(req)}
          className="px-3 py-1.5 text-xs font-medium text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-50">
          Reverse
        </button>
      )}
    </div>
  )
}

/** "1 Jul 2026 – 30 Jun 2027" from two YYYY-MM-DD strings. */
function formatFyRange(start: string, end: string) {
  const f = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  return `${f(start)} – ${f(end)}`
}

export default function Leaves() {
  const queryClient = useQueryClient()
  const [showApply, setShowApply] = useState(false)
  const [reversalOf, setReversalOf] = useState<LeaveRequest | undefined>(undefined)

  // All three are the signed-in user's own data, so the cache is keyed by user id:
  // switching accounts in the same tab never shows the previous person's numbers.
  const { user } = useAuthStore()
  const uid = user?.id
  // Fiscal year shown (cards + history). Defaults to the current one, so the history
  // always matches the "Used" numbers on the cards.
  const { data: settings } = useQuery({ queryKey: ['leaves', 'settings', uid], queryFn: getLeaveSettings, enabled: !!uid })
  const [fyKey, setFyKey] = useState<number | null>(null)
  const currentFy = settings?.current_fiscal_year
  const years = settings?.fiscal_years?.length ? settings.fiscal_years : currentFy ? [currentFy] : []
  const fy = years.find((y) => y.key === fyKey) || currentFy
  const isCurrentFy = !fy || !currentFy || fy.key === currentFy.key
  const isPastFy = !!fy && !!currentFy && fy.key < currentFy.key

  const { data: balance } = useQuery({
    queryKey: ['leaves', 'balance', uid, isCurrentFy ? 'current' : fy?.key],
    queryFn: () => getMyBalance(isCurrentFy ? undefined : fy?.key),
    enabled: !!uid,
  })
  const { data: allRequests = [] } = useQuery({ queryKey: ['leaves', 'me', uid], queryFn: () => listMyRequests(), enabled: !!uid })
  // A request belongs to the fiscal year its leave dates fall in (as the quota counts
  // it). Pending ones are always shown, so nothing awaiting approval is hidden.
  const inFy = (r: LeaveRequest) => !fy || (r.start_date <= fy.end && r.end_date >= fy.start)
  const requests = allRequests.filter((r) => inFy(r) || r.status === 'pending')
  const hiddenPending = allRequests.filter((r) => !inFy(r) && r.status === 'pending').length
  // Employee-safe source for the balance cards' labels/colours (the org-wide
  // /organization/leave-types endpoint is restricted to leave managers).
  const { data: leaveTypes = [] } = useQuery({ queryKey: ['leave-types', 'eligible', uid], queryFn: getEligibleTypes, enabled: !!uid })

  const cancelMut = useMutation({
    mutationFn: cancelRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leaves'] })
    },
  })

  const openReverse = (req: LeaveRequest) => {
    setReversalOf(req)
    setShowApply(true)
  }
  const closeApply = () => {
    setShowApply(false)
    setReversalOf(undefined)
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leaves</h1>
          <p className="text-sm text-gray-500">Apply for leave and track your requests</p>
        </div>
        <button onClick={() => setShowApply(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#EC5B2C] text-white rounded-lg hover:bg-[#EA4913] text-sm font-medium">
          <Plus className="h-4 w-4" /> Apply for Leave
        </button>
      </div>

      {/* Balance cards */}
      {balance && (
        <SectionCard
          title={<>Leave Summary {fy && <span className="ml-1 font-normal text-gray-400">· {fy.label}</span>}</>}
          action={years.length > 1 ? (
            <select value={fy?.key ?? ''} onChange={(e) => setFyKey(Number(e.target.value))} aria-label="Fiscal year"
              className="rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/30">
              {years.map((y) => (
                <option key={y.key} value={y.key}>{y.label}{y.key === currentFy?.key ? ' (current)' : ''}</option>
              ))}
            </select>
          ) : undefined}
        >
          {fy && (
            <p className="-mt-1 mb-4 text-xs text-gray-400">
              {formatFyRange(fy.start, fy.end)}{isPastFy ? ' · past year' : ''}
            </p>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {leaveTypes.filter((lt) => balance[lt.key]).map((lt) => (
              <BalanceCard key={lt.key} label={lt.label} color={lt.color} isWfh={lt.key === 'wfh'} pastYear={isPastFy} {...balance[lt.key]} />
            ))}
          </div>
        </SectionCard>
      )}

      {/* Requests */}
      <SectionCard
        title={<>My Requests <span className="ml-1 font-normal text-gray-400">({requests.length}){fy ? ` · ${fy.label}` : ''}</span></>}
        bodyClassName={requests.length === 0 ? '' : 'p-0'}
      >
        {hiddenPending > 0 && requests.length > 0 && (
          <p className="px-4 pt-3 text-xs text-gray-400">Includes {hiddenPending} pending request{hiddenPending === 1 ? '' : 's'} from another fiscal year.</p>
        )}
        {requests.length === 0 ? (
          <EmptyState label={fy ? `No leave requests in ${fy.label}.` : 'No leave requests yet.'} />
        ) : (
          <div className="divide-y divide-gray-100">
            {requests.map((r) => (
              <RequestRow key={r.id} req={r} onCancel={(id) => cancelMut.mutate(id)} onReverse={openReverse} />
            ))}
          </div>
        )}
      </SectionCard>

      {showApply && (
        <ApplyModal
          onClose={closeApply}
          onCreated={() => queryClient.invalidateQueries({ queryKey: ['leaves'] })}
          reversalOf={reversalOf}
        />
      )}
    </div>
  )
}
